import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { projectsData, projectStats, companyInfo } from '../data';


const Projects = () => {
  const [filter, setFilter] = useState('All');
  const projects = projectsData || [];
  const stats = projectStats || [];

  const categories = ['All', ...new Set(projects.map(p => p.category))];
  
  const filteredProjects = filter === 'All' 
    ? projects 
    : projects.filter(p => p.category === filter);

  return (
    <>
      <Helmet>
        <title>Our Projects | Al Abeer Diamond</title>
        <meta name="description" content={`Browse our portfolio of successful projects in construction, MEP, and industrial services across Saudi Arabia.`} />
        <meta name="keywords" content="construction projects saudi arabia, building projects, MEP projects, industrial projects" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[40vh] flex items-center overflow-hidden bg-secondary">
        <div className="absolute inset-0 bg-hero-gradient opacity-90"></div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-2 bg-primary/20 text-primary-light rounded-full text-sm font-medium font-oswald tracking-wider mb-4">
              Our Portfolio
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white font-oswald leading-tight">
              Featured <br />
              <span className="text-primary-light">Projects</span>
            </h1>
            <p className="text-gray-300 text-lg mt-4 max-w-2xl">
              From civil works and MEP systems to cleaning and industrial developments, we execute every project with innovation, safety, and professionalism.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-gray-50">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {stats.map((stat) => (
              <motion.div
                key={stat.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: stat.id * 0.1 }}
                className="bg-white rounded-2xl p-4 text-center shadow-soft"
              >
                <div className="text-2xl md:text-3xl font-bold text-primary font-oswald">
                  {stat.number}
                </div>
                <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          {/* Filter Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="flex flex-wrap justify-center gap-2 mb-12"
          >
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setFilter(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  filter === category
                    ? 'bg-primary text-white shadow-lg'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group bg-white rounded-2xl overflow-hidden shadow-soft card-hover border border-gray-100"
              >
                <div className="relative overflow-hidden h-64">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-secondary/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-primary/90 text-white text-xs rounded-full font-medium">
                      {project.category}
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Link 
                      to={`/projects/${project.id}`} 
                      className="text-white text-sm font-medium hover:text-primary-light transition-colors"
                    >
                      View Details <i className="uil uil-arrow-right ml-1"></i>
                    </Link>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-secondary font-oswald mb-2">
                    {project.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {project.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary font-oswald">
              Have a Project in Mind?
            </h2>
            <p className="text-gray-600 text-lg mt-4 max-w-2xl mx-auto">
              Let's discuss how we can bring your vision to life
            </p>
            <Link to="/contact" className="inline-block mt-6 btn-primary">
              Start Your Project <i className="uil uil-arrow-right ml-2"></i>
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Projects;